/**
 * useAutoSave - Unified auto-save hook with change detection, debouncing, and blur support.
 *
 * This hook encapsulates the common pattern of:
 * 1. Watching form values for changes
 * 2. Detecting which fields changed since last save
 * 3. Building a selective payload (only changed fields)
 * 4. Debouncing saves to avoid excessive API calls
 * 5. Supporting immediate save on blur
 *
 * @example
 * ```tsx
 * const { status, saveNow } = useAutoSave({
 *   values: { name: currentName, due_date: currentDueDate },
 *   fields: [
 *     { name: 'name', skipIf: (v) => !v?.trim() },
 *     { name: 'due_date', transform: (v) => v || null },
 *   ],
 *   saveFn: (payload) => updateFeature(featureId, payload),
 *   delay: 1500,
 *   enabled: !!simulationId,
 *   onError: handleError,
 * });
 *
 * // In onBlur handler:
 * const handleBlur = useCallback(() => saveNow(), [saveNow]);
 * ```
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { valuesEqual } from '@/lib/utils';

export type SaveStatus = 'idle' | 'draft' | 'saving' | 'saved' | 'error';

const DEFAULT_DEBOUNCE_DELAY = 1500;
const SAVED_INDICATOR_DURATION = 1500;

export interface FieldConfig<TValues> {
  /** Field name from the values object */
  name: keyof TValues;
  /** Transform function to apply before comparison and saving (e.g., string -> number) */
  transform?: (value: unknown) => unknown;
  /** Skip saving if this returns true (e.g., skip empty names) */
  skipIf?: (transformedValue: unknown) => boolean;
}

export interface UseAutoSaveOptions<TValues> {
  /** Current form values (typically from useWatch) */
  values: TValues;
  /** Fields to track for changes */
  fields: FieldConfig<TValues>[];
  /** Async function to save changed fields (return value is ignored) */
  saveFn: (payload: Partial<TValues>) => Promise<unknown>;
  /** Debounce delay in milliseconds (default: 1500) */
  delay?: number;
  /** Gate saves - when false, no saves will be triggered (e.g., when simulationId is null) */
  enabled?: boolean;
  /** Called when save fails */
  onError?: (error: unknown) => void;
  /** Called when save succeeds */
  onSuccess?: () => void;
}

export interface UseAutoSaveReturn<TValues = Record<string, unknown>> {
  /** Current save status */
  status: SaveStatus;
  /** Save immediately (cancels pending debounced save) - call this on blur */
  saveNow: () => Promise<void>;
  /** Cancel pending save without saving */
  cancel: () => void;
  /** Whether any field has changed from baseline */
  isDirty: boolean;
  /** Reset baseline to current values (call after external data refresh) */
  resetBaseline: () => void;
  /**
   * Record `value` as what the server already holds for `field`. Call it for
   * a value written into the form from the server (the cache sync), so it is
   * not mistaken for an edit and saved back (shipcast-d8r.40).
   */
  rebase: (field: keyof TValues, value: unknown) => void;
}

/**
 * Unified auto-save hook with change detection, debouncing, and blur support.
 *
 * Features:
 * - Tracks baseline values and detects changes per-field
 * - Builds selective payloads (only changed fields)
 * - Supports field transforms (e.g., string → number)
 * - Supports skip conditions (e.g., skip empty names)
 * - Debounced saves with configurable delay
 * - `saveNow()` for immediate save on blur
 * - Status tracking: idle → draft → saving → saved → idle (or error)
 * - Cleanup on unmount
 */
export function useAutoSave<TValues extends Record<string, unknown>>({
  values,
  fields,
  saveFn,
  delay = DEFAULT_DEBOUNCE_DELAY,
  enabled = true,
  onError,
  onSuccess,
}: UseAutoSaveOptions<TValues>): UseAutoSaveReturn<TValues> {
  // Save lifecycle, independent of dirtiness: 'idle' | 'saving' | 'saved' | 'error'.
  // The public `status` derives 'draft' from `isDirty` (see below), so the watch
  // effect never has to write a status literal — keeping React Compiler's
  // set-state-in-effect rule satisfied. See shipcast-7i0.
  const [lifecycle, setLifecycle] = useState<SaveStatus>('idle');

  // Whether any tracked field currently differs from the saved baseline.
  // Derived state (updated from the ref-based baseline in the watch effect and
  // the save/reset paths) rather than computed during render, so the baseline
  // ref is never read during render.
  const [isDirty, setIsDirty] = useState(false);

  // Track mounted state to avoid state updates after unmount
  const isMountedRef = useRef(true);

  // Baseline: stores the last-saved transformed value for each field
  const baselineRef = useRef<Map<keyof TValues, unknown>>(new Map());

  // Track whether we've initialized the baseline
  const isInitializedRef = useRef(false);

  // Timer for debounced saves
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timer for resetting "saved" status back to "idle"
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
    };
  }, []);

  /**
   * Transform a field value using its config.
   */
  const transformValue = useCallback(
    (fieldName: keyof TValues, value: unknown): unknown => {
      const config = fields.find((f) => f.name === fieldName);
      return config?.transform ? config.transform(value) : value;
    },
    [fields],
  );

  /**
   * Check if a field should be skipped based on its config.
   */
  const shouldSkipField = useCallback(
    (fieldName: keyof TValues, transformedValue: unknown): boolean => {
      const config = fields.find((f) => f.name === fieldName);
      return config?.skipIf ? config.skipIf(transformedValue) : false;
    },
    [fields],
  );

  /**
   * Get transformed values for all fields.
   */
  const getTransformedValues = useCallback((): Map<keyof TValues, unknown> => {
    const map = new Map<keyof TValues, unknown>();
    for (const field of fields) {
      const raw = values[field.name];
      const transformed = transformValue(field.name, raw);
      map.set(field.name, transformed);
    }
    return map;
  }, [fields, values, transformValue]);

  /**
   * Build payload with only changed fields.
   */
  const buildPayload = useCallback((): Partial<TValues> | null => {
    const payload: Partial<TValues> = {};
    let hasChanges = false;

    for (const field of fields) {
      const raw = values[field.name];
      const transformed = transformValue(field.name, raw);
      const baseline = baselineRef.current.get(field.name);

      // Check if value changed from baseline (deep comparison for arrays/objects)
      if (!valuesEqual(transformed, baseline)) {
        // Check skip condition
        if (!shouldSkipField(field.name, transformed)) {
          (payload as Record<keyof TValues, unknown>)[field.name] = transformed;
          hasChanges = true;
        }
      }
    }

    return hasChanges ? payload : null;
  }, [fields, values, transformValue, shouldSkipField]);

  /**
   * Execute save with the current payload.
   */
  const executeSave = useCallback(async (): Promise<void> => {
    if (!isMountedRef.current || !enabled) return;

    const payload = buildPayload();
    if (!payload) return;

    // Update baseline before saving (optimistic)
    const currentTransformed = getTransformedValues();
    for (const [key, value] of currentTransformed) {
      baselineRef.current.set(key, value);
    }
    // Baseline now matches current values, so nothing is pending.
    setIsDirty(false);

    setLifecycle('saving');

    try {
      await saveFn(payload);
      if (!isMountedRef.current) return;

      setLifecycle('saved');
      onSuccess?.();

      // Clear any existing saved timer
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }

      // Reset to idle after showing "saved" briefly
      savedTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setLifecycle('idle');
        }
      }, SAVED_INDICATOR_DURATION);
    } catch (error) {
      if (!isMountedRef.current) return;
      setLifecycle('error');
      onError?.(error);
    }
  }, [enabled, buildPayload, getTransformedValues, saveFn, onSuccess, onError]);

  /**
   * Initialize baseline on first render with values.
   */
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      const transformed = getTransformedValues();
      for (const [key, value] of transformed) {
        baselineRef.current.set(key, value);
      }
    }
  }, [getTransformedValues]);

  /**
   * Watch for value changes and trigger debounced save.
   */
  useEffect(() => {
    // Don't trigger saves if not initialized or not enabled
    if (!isInitializedRef.current || !enabled) return;

    // Reflect dirtiness from the (ref-derived) payload. The public `status`
    // derives 'draft' from this, so no status literal is written in the effect.
    const payload = buildPayload();
    setIsDirty(payload !== null);

    if (!payload) {
      return;
    }

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set debounced save
    timerRef.current = setTimeout(() => {
      void executeSave();
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [values, enabled, buildPayload, executeSave, delay]);

  /**
   * Save immediately (for blur handlers).
   */
  const saveNow = useCallback(async (): Promise<void> => {
    // Cancel pending debounced save
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    await executeSave();
  }, [executeSave]);

  /**
   * Cancel pending save without saving.
   */
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Cancel only the in-flight debounce. If changes still remain, reschedule so
    // the save eventually runs — the watch effect won't re-run on its own here
    // since cancel() touches no reactive dependency. (Production never calls
    // cancel(); it exists for completeness and is covered by tests.)
    if (isMountedRef.current && enabled && buildPayload() !== null) {
      timerRef.current = setTimeout(() => {
        void executeSave();
      }, delay);
    }
  }, [enabled, buildPayload, executeSave, delay]);

  /**
   * Reset baseline to current values (call after external data refresh).
   */
  const resetBaseline = useCallback(() => {
    const transformed = getTransformedValues();
    for (const [key, value] of transformed) {
      baselineRef.current.set(key, value);
    }
    if (isMountedRef.current) {
      setLifecycle('idle');
      setIsDirty(false);
    }
  }, [getTransformedValues]);

  /**
   * Take `value` as the field's baseline: it came from the server, so it is not
   * a change. The form re-renders with it right after; nothing is saved.
   */
  const rebase = useCallback(
    (field: keyof TValues, value: unknown) => {
      baselineRef.current.set(field, transformValue(field, value));
    },
    [transformValue],
  );

  // Public status: a pending edit always presents as 'draft'; otherwise the
  // save lifecycle ('idle' | 'saving' | 'saved' | 'error') shows through.
  const status: SaveStatus = isDirty ? 'draft' : lifecycle;

  return {
    status,
    saveNow,
    cancel,
    isDirty,
    resetBaseline,
    rebase,
  };
}
